import { Trans, useTranslation } from 'react-i18next'
import type { DefaultNamespace } from 'i18next'
import { Box, Grid, Paper, Typography, Link } from '@mui/material'
import { CheckCircle } from '@dhl/icons'
import { useTranslationContext, useQueryParam, type TranslationContext } from '~/hooks'
import type { LabelType, BusinessUnitCode } from '~/model'
import { BasePage } from '~/pages'
import { getServicePointUrl, getNextSteps } from '~/helpers'
import { languageForBusinessUnit } from '~/i18n'

export const ServicePointSuccessPage = () => {
  const { t, i18n } = useTranslation()

  const businessUnit: BusinessUnitCode = useQueryParam('businessUnit') as BusinessUnitCode
  const postalOffice = useQueryParam('postalOffice') === 'true'
  const cccLocatorUrl = useQueryParam('cccLocatorUrl')
  const countryCode: string = useQueryParam('countryCode') || 'NL'
  const labelType = useQueryParam('labelType') as LabelType
  const tComponents = {
    // eslint-disable-next-line jsx-a11y/heading-has-content
    bold: <Typography component="strong" sx={{ fontFamily: 'Delivery Bold', fontWeight: 700 }} />,
  }
  const languages = languageForBusinessUnit(businessUnit)
  const translationContext = useTranslationContext(countryCode === 'BE' ? 'dhl-be' : businessUnit)
  const isCcc = businessUnit === 'ccc'
  const steps = getNextSteps(
    isCcc,
    t('success.servicepoint.tips_url', translationContext),
    isCcc && cccLocatorUrl ? cccLocatorUrl : getServicePointUrl(countryCode, i18n.language),
    postalOffice,
    labelType
  )
  return (
    <BasePage testId="service-point-return-success-page" isLoading={false} languages={languages}>
      <Box mt={4}>
        <Paper
          sx={({ palette: { grey } }) => ({
            marginBottom: 2,
            borderRadius: '3px',
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderBottomColor: grey[300],
          })}
        >
          <Grid container direction="row" alignItems="center" sx={{ padding: 2 }}>
            <Grid>
              <CheckCircle
                sx={({ spacing, palette: { success } }) => ({
                  marginRight: spacing(),
                  fontSize: '24px',
                  color: success.main,
                })}
              />
            </Grid>
            <Grid>
              <Typography data-test="success-title">
                <Trans
                  i18nKey={labelType === 'QrCode' ? 'success.servicepoint.qrcode.title' : 'success.servicepoint.title'}
                  components={tComponents}
                />
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        <Paper
          sx={({ palette: { grey } }) => ({
            marginBottom: '2px',
            borderRadius: '3px',
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderBottomColor: grey[300],
          })}
        >
          <Grid container direction="column">
            <Grid
              sx={({ palette: { grey } }) => ({
                padding: 2,
                borderBottomWidth: '1px',
                borderBottomStyle: 'solid',
                borderBottomColor: grey[300],
              })}
            >
              <Typography data-test="success-title" sx={{ fontFamily: 'Delivery Bold', fontWeight: 700 }}>
                {t('success.servicepoint.further', translationContext)}
              </Typography>
            </Grid>
            {steps.map(({ key, stepLink }, i) => {
              const isLastItem = i === steps.length - 1
              return (
                <Grid
                  key={key}
                  container
                  direction="row"
                  sx={({ palette: { grey } }) => ({
                    padding: 2,
                    ...(isLastItem
                      ? {}
                      : { borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: grey[300] }),
                  })}
                  alignItems="center"
                >
                  <Grid sx={{ marginRight: 1 }}>
                    <Box
                      sx={({ palette: { grey } }) => ({
                        backgroundColor: grey[300],
                        borderRadius: 2,
                        width: 4,
                        height: 4,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      })}
                      component="div"
                    >
                      {i + 1}
                    </Box>
                  </Grid>
                  <Grid>
                    <Typography>
                      <Trans<string, DefaultNamespace, undefined, TranslationContext['context'], TranslationContext>
                        i18nKey={key}
                        components={tComponents}
                        tOptions={translationContext}
                      />
                    </Typography>
                  </Grid>
                  {stepLink && (
                    <Grid sx={{ marginLeft: 'auto' }}>
                      <Link
                        href={stepLink.link}
                        sx={{
                          color: `primary.main`,
                          marginLeft: { xs: 5, sm: 'auto' },
                        }}
                        download={stepLink.download ? 'label.pdf' : undefined}
                        target={stepLink.download ? '_self' : '_blank'}
                        rel={stepLink.download ? '' : 'noopener noreferrer'}
                      >
                        <Typography data-test={stepLink.dataTest ?? ''}>
                          {t(stepLink.key, translationContext)}
                        </Typography>
                      </Link>
                    </Grid>
                  )}
                </Grid>
              )
            })}
          </Grid>
        </Paper>
      </Box>
    </BasePage>
  )
}
